import { useEffect, useRef, useState } from "react";
import { PanelNav } from "../Bootoverlay/Bootoverlay";
import AppPromoPopup from "./Apppromopopup";

// ─── RPM GAUGE ────────────────────────────────────────
function RPMGauge() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;
        const size = 100;
        canvas.width = size * dpr;
        canvas.height = size * dpr;
        ctx.scale(dpr, dpr);
        const cx = size / 2, cy = size / 2, r = size * 0.38;
        let rpm = 0, targetRPM = 40;
        let animId;

        function draw(val) {
            ctx.clearRect(0, 0, size, size);
            ctx.beginPath();
            ctx.arc(cx, cy, r + 4, 0, Math.PI * 2);
            ctx.strokeStyle = "rgba(255,56,0,0.08)";
            ctx.lineWidth = 2;
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(cx, cy, r, Math.PI * 0.75, Math.PI * 2.25);
            ctx.strokeStyle = "rgba(255,255,255,0.05)";
            ctx.lineWidth = size * 0.06;
            ctx.lineCap = "round";
            ctx.stroke();
            const angle = Math.PI * 0.75 + (val / 100) * Math.PI * 1.5;
            const grad = ctx.createLinearGradient(0, 0, size, size);
            grad.addColorStop(0, val > 75 ? "#FF3800" : "#00D4FF");
            grad.addColorStop(1, "#FF3800");
            ctx.beginPath();
            ctx.arc(cx, cy, r, Math.PI * 0.75, angle);
            ctx.strokeStyle = grad;
            ctx.lineWidth = size * 0.06;
            ctx.lineCap = "round";
            ctx.stroke();
            ctx.fillStyle = "#E8E6E1";
            ctx.font = `bold ${size * 0.2}px 'Chakra Petch', monospace`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(Math.round(val), cx, cy - 4);
            ctx.fillStyle = "rgba(255,255,255,0.25)";
            ctx.font = `${size * 0.1}px 'Space Mono', monospace`;
            ctx.fillText("RPM", cx, cy + 10);
        }

        function animate() {
            rpm += (targetRPM - rpm) * 0.06;
            draw(rpm);
            animId = requestAnimationFrame(animate);
        }

        const iv = setInterval(() => { targetRPM = 20 + Math.random() * 70; }, 2200);
        animate();
        return () => { cancelAnimationFrame(animId); clearInterval(iv); };
    }, []);

    return (
        <div className="vk-rpm-gauge">
            <canvas ref={canvasRef} width="100" height="100" />
            <span className="vk-rpm-label">RPM</span>
        </div>
    );
}

// ─── PANEL 0: HERO ────────────────────────────────────
export default function PanelHero({ onNav }) {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const MAX_PULL = 80;
        const RESISTANCE = 0.25;

        let pull = 0;
        let ticking = false;
        let hasSnapped = false;

        let lastWheelTime = 0;
        let watching = false;

        const hero = document.querySelector(".vk-hero-move");
        const overlay = document.querySelector(".vk-hero-overlay");

        function applyPull(val) {
            if (!hero) return;
            hero.style.transition = "none";
            hero.style.transform = `translateY(${val}px) scale(${1 + val * 0.0009})`;
            hero.style.filter = `blur(${val * 0.04}px)`;
            if (overlay) {
                overlay.style.transition = "none";
                overlay.style.opacity = String(1 - val * 0.008);
            }
        }

        function springBack() {
            pull = 0;
            ticking = false;
            watching = false;
            if (!hero) return;
            hero.style.transition =
                "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1), filter 0.6s cubic-bezier(0.22, 1, 0.36, 1)";
            hero.style.transform = "translateY(0px) scale(1)";
            hero.style.filter = "blur(0px)";
            if (overlay) {
                overlay.style.transition = "opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1)";
                overlay.style.opacity = "1";
            }
        }

        function watchScrollEnd() {
            const now = performance.now();
            if (now - lastWheelTime > 120) {
                if (pull > 0) springBack();
                watching = false;
                return;
            }
            requestAnimationFrame(watchScrollEnd);
        }

        function onWheel(e) {
            if (window.scrollY > 0) return;
            if (e.deltaY <= 0) {
                if (pull > 0) springBack();
                return;
            }
            lastWheelTime = performance.now();
            hasSnapped = false;
            pull += e.deltaY * RESISTANCE;
            pull = Math.min(pull, MAX_PULL);
            if (pull >= MAX_PULL && !hasSnapped) {
                hasSnapped = true;
                springBack();
                window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
                return;
            }
            if (!watching) {
                watching = true;
                requestAnimationFrame(watchScrollEnd);
            }
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(() => {
                    applyPull(pull);
                    ticking = false;
                });
            }
        }

        function onScroll() {
            pull = 0;
            hasSnapped = false;
            watching = false;
            if (hero) {
                hero.style.transition =
                    "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), filter 0.4s cubic-bezier(0.22, 1, 0.36, 1)";
                hero.style.transform = "translateY(0px) scale(1)";
                hero.style.filter = "blur(0px)";
            }
            if (overlay) {
                overlay.style.transition = "opacity 0.4s ease";
                overlay.style.opacity = "1";
            }
        }

        window.addEventListener("wheel", onWheel, { passive: true });
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("wheel", onWheel);
            window.removeEventListener("scroll", onScroll);
        };
    }, []);

    const telemData = [
        ["UNIT", "VK-001"], ["STATUS", "ONLINE"], ["DEPLOYMENT", "WINTER 2026"],
        ["LOCATION", "LAS VEGAS NV"], ["MISSIONS", "4 ACTIVE"],
        ["UNIT", "VK-001"], ["STATUS", "ONLINE"], ["DEPLOYMENT", "WINTER 2026"],
        ["LOCATION", "LAS VEGAS NV"], ["MISSIONS", "4 ACTIVE"],
    ];

    return (
        <>
            <section className="vk-panel" id="panel-hero">

                <div className={`vk-hero-bg vk-hero-move ${isLoaded ? "is-loaded" : "is-loading"}`}>
                    <img
                        src="/images/hero-placeholder.png"
                        alt="Vegas Karts on the Las Vegas Strip"
                        className="vk-hero-img"
                        onLoad={() => setIsLoaded(true)}
                        loading="eager"
                    />
                    <div className="vk-hero-overlay" />
                    <div className="vk-scanlines" />
                    <div className="vk-hero-noise" />
                </div>

                <div className="vk-hud-corner vk-hud-tl" />
                <div className="vk-hud-corner vk-hud-tr" />
                <div className="vk-hud-corner vk-hud-bl" />
                <div className="vk-hud-corner vk-hud-br" />

                <div className="vk-telem-bar">
                    <div className="vk-telem-track">
                        {[...telemData, ...telemData].map(([label, val], i) => (
                            <span key={i} className="vk-telem-item">
                                <span className="vk-telem-label">{label}</span>
                                <span className="vk-telem-sep">//</span>
                                <span className="vk-telem-val">{val}</span>
                                <span className="vk-telem-dot">·</span>
                            </span>
                        ))}
                    </div>
                </div>

                <div className="vk-hero-content">
                    <div className="vk-hero-eyebrow">
                        <span className="vk-eyebrow-tags">
                            <span className="vk-eyebrow-dot" />
                            SYSTEM ONLINE
                        </span>
                        <span className="vk-eyebrow-sub vk-eyebrow-sub--highlight">
                            DRIVER REGISTRATION OPEN
                        </span>
                    </div>

                    <h1 className="vk-hero-title">
                        <span className="vk-title-line1">VEGAS</span>
                        <span className="vk-title-line2">KARTS</span>
                    </h1>

                    <p className="vk-hero-sub">
                        Americas first street legal go-kart tour.
                    </p>

                    <div className="vk-hero-ctas">
                        <button className="vk-btn-primary" onClick={() => onNav(4)}>
                            CREATE YOUR DRIVER ▶
                        </button>
                        <button className="vk-btn-secondary" onClick={() => onNav(3)}>
                            Book your tour now ▶
                        </button>
                    </div>

                    <div className="vk-hero-meta">
                        <span className="vk-meta-item">
                            <span className="vk-meta-dot orange" />
                            PRE-ORDERS OPEN
                        </span>
                        <span className="vk-meta-sep">·</span>
                        <span className="vk-meta-item">
                            <span className="vk-meta-dot blue" />
                            WINTER 2026 DEPLOYMENT
                        </span>
                    </div>
                </div>

                <div className="vk-scroll-hint">
                    <span>SCROLL TO ADVANCE</span>
                    <div className="vk-scroll-arrow">
                        <span>▶</span><span>▶</span><span>▶</span>
                    </div>
                </div>

            </section>
            {/* AppPromoPopup is now managed in VegasKarts — removed from here */}
        </>
    );
}